// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — GapCard
// Enhanced to show:
//   • Buffer breakdown (raw gap → allowance → adjusted gap)
//   • Labor shift details for labor_inefficiency type
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  AlertTriangle, Check, Package, DollarSign, TrendingDown,
  Radar as RadarIcon, Users, Shield, ChevronDown, ChevronUp, Coffee
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Badge } from '@components/ui/Primitives';

const GAP_TYPE_ICONS = {
  inventory_mismatch: Package,
  revenue_leak: DollarSign,
  unexplained_cost: AlertTriangle,
  theoretical_vs_actual: RadarIcon,
  discount_anomaly: TrendingDown,
  labor_inefficiency: Users
};

const SEVERITY_VARIANTS = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral'
};

export function GapCard({ gap, onResolve }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const [expanded, setExpanded] = useState(false);

  const Icon = GAP_TYPE_ICONS[gap.gap_type] || AlertTriangle;

  const severityColor =
    gap.severity === 'critical' ? theme.danger :
    gap.severity === 'high' ? theme.warning :
    gap.severity === 'medium' ? theme.info :
    theme.textTertiary;

  const gapTypeLabel =
    gap.gap_type === 'inventory_mismatch' ? t.gapInventory :
    gap.gap_type === 'revenue_leak' ? t.gapRevenue :
    gap.gap_type === 'unexplained_cost' ? t.gapCost :
    gap.gap_type === 'labor_inefficiency' ? (lang === 'ar' ? 'كفاءة العمالة' : 'Labor Efficiency') :
    gap.gap_type;

  const gapAmount = parseFloat(gap.gap_amount || 0);
  const expectedValue = parseFloat(gap.expected_value || 0);
  const actualValue = parseFloat(gap.actual_value || 0);
  const gapPct = parseFloat(gap.gap_percentage || 0);
  const monetaryLoss = parseFloat(gap.estimated_monetary_loss || 0);

  const hasBufferDetail = gap.buffer_applied && gap.buffer_breakdown?.length > 0;
  const hasShiftDetail = gap.shift_detail;
  const hasDetail = hasBufferDetail || hasShiftDetail;

  return (
    <Card style={{
      padding: 20,
      [isRTL ? 'borderRight' : 'borderLeft']: `3px solid ${severityColor}`
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
            flexWrap: 'wrap'
          }}>
            <Icon size={18} color={severityColor} />
            <Badge variant={SEVERITY_VARIANTS[gap.severity] || 'info'}>
              {t[`severity_${gap.severity}`] || gap.severity}
            </Badge>
            <Badge variant="info">{gapTypeLabel}</Badge>
            {gap.is_computed && (
              <Badge variant="primary">
                {lang === 'ar' ? 'محسوب آني' : 'Live-computed'}
              </Badge>
            )}
            {hasBufferDetail && (
              <Badge variant="success">
                <Shield size={10} /> {t.operationalBuffer}
              </Badge>
            )}
          </div>

          <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: theme.text,
            marginBottom: 6
          }}>
            {lang === 'ar' ? gap.title_ar : gap.title_en}
          </div>

          <p style={{
            margin: '0 0 12px',
            fontSize: 13,
            color: theme.textSecondary,
            lineHeight: 1.6
          }}>
            {lang === 'ar' ? gap.description_ar : gap.description_en}
          </p>

          <div style={{
            display: 'flex',
            gap: 20,
            fontSize: 12,
            color: theme.textSecondary,
            flexWrap: 'wrap',
            paddingTop: 8,
            borderTop: `1px solid ${theme.borderLight}`
          }}>
            <div>
              <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 2 }}>
                {t.expectedValue}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                {expectedValue.toFixed(1)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 2 }}>
                {t.actualValue}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                {actualValue.toFixed(1)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 2 }}>
                {hasBufferDetail ? t.adjustedGap : t.gapAmount}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.danger }}>
                {gapAmount.toFixed(1)}{gapPct ? ` (${gapPct > 0 ? '+' : ''}${gapPct.toFixed(1)}%)` : ''}
              </div>
              {hasBufferDetail && (
                <div style={{ fontSize: 10, color: theme.textTertiary, marginTop: 2 }}>
                  {lang === 'ar' ? 'الأصلي:' : 'Raw:'} {gap.raw_gap_amount?.toFixed(1)}
                </div>
              )}
            </div>
            {monetaryLoss > 0 && (
              <div>
                <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 2 }}>
                  {t.estimatedLoss}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.danger }}>
                  {t.sar} {monetaryLoss.toFixed(0)}
                </div>
              </div>
            )}
          </div>

          {hasDetail && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(p => !p)}
              style={{ marginTop: 12, padding: 0 }}
            >
              {lang === 'ar' ? 'التفاصيل' : 'Details'}
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </Button>
          )}

          {expanded && hasBufferDetail && (
            <div style={{
              marginTop: 12,
              padding: 14,
              background: theme.successDim,
              borderRadius: 10,
              border: `1px solid ${theme.primaryBorder}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 10,
                fontSize: 12,
                fontWeight: 700,
                color: theme.success
              }}>
                <Shield size={13} />
                {lang === 'ar' ? 'تفاصيل الهامش التشغيلي' : 'Operational Buffer Applied'}
              </div>

              <div style={{
                fontSize: 11,
                color: theme.textSecondary,
                marginBottom: 10,
                lineHeight: 1.6
              }}>
                {lang === 'ar'
                  ? `الفجوة الأصلية كانت ${gap.raw_gap_amount?.toFixed(1)}، لكن بعد خصم الاستهلاك المشروع تبقى ${gap.adjusted_gap_amount?.toFixed(1)} فقط كفجوة حقيقية.`
                  : `Raw gap was ${gap.raw_gap_amount?.toFixed(1)}, but after deducting legitimate consumption only ${gap.adjusted_gap_amount?.toFixed(1)} remains as true gap.`
                }
              </div>

              {gap.buffer_breakdown.map((b, i) => (
                <div key={i} style={{
                  padding: '8px 10px',
                  background: theme.bgCard,
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 11,
                  color: theme.textSecondary,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span>{lang === 'ar' ? b.reason_ar : b.reason_en}</span>
                  <span style={{ fontWeight: 700, color: theme.success, whiteSpace: 'nowrap' }}>
                    -{b.allowance_qty.toFixed(2)}
                  </span>
                </div>
              ))}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: theme.text,
                fontWeight: 700,
                marginTop: 8,
                paddingTop: 8,
                borderTop: `1px solid ${theme.border}`
              }}>
                <span>{lang === 'ar' ? 'الخسارة الأصلية' : 'Raw loss'}: </span>
                <span>{t.sar} {gap.raw_estimated_loss?.toFixed(0)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: theme.success,
                fontWeight: 700
              }}>
                <span>{lang === 'ar' ? 'الخسارة الحقيقية' : 'True loss'}:</span>
                <span>{t.sar} {gap.adjusted_estimated_loss?.toFixed(0)}</span>
              </div>
            </div>
          )}

          {expanded && hasShiftDetail && (
            <div style={{
              marginTop: 12,
              padding: 14,
              background: theme.bgInput,
              borderRadius: 10,
              border: `1px solid ${theme.border}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 10,
                fontSize: 12,
                fontWeight: 700,
                color: theme.text
              }}>
                <Coffee size={13} />
                {lang === 'ar' ? 'تفاصيل الوردية' : 'Shift Breakdown'}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: theme.textTertiary }}>{lang === 'ar' ? 'الموظفون' : 'Staff'}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{gap.shift_detail.staff_count}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: theme.textTertiary }}>{lang === 'ar' ? 'ساعات الوردية' : 'Hours'}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{gap.shift_detail.shift_hours}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: theme.textTertiary }}>{lang === 'ar' ? 'أجر الساعة' : 'Hourly wage'}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{t.sar} {gap.shift_detail.avg_hourly_wage}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: theme.textTertiary }}>{t.revenuePerLaborSAR}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: gap.shift_detail.revenue_per_labor_sar < 2 ? theme.danger : theme.success }}>
                    {gap.shift_detail.revenue_per_labor_sar.toFixed(2)}×
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {onResolve && (
          <Button size="sm" variant="outline" onClick={() => onResolve(gap.id)}>
            <Check size={12} /> {t.resolveGap}
          </Button>
        )}
      </div>
    </Card>
  );
}
